/**
 * Focus director — the *temporal* brain on top of the camera operator.
 *
 * The `CameraOperator` answers "given a target, where should the viewport be?"
 * (pure geometry + a visibility gate). The director answers "given what's
 * happening *over time*, should we frame, hold, or drift back?" — the intent
 * layer the user described:
 *
 *   - A really small edit doesn't move the view at all.
 *   - Sustained editing for ~a couple of seconds eases IN on what you're editing.
 *   - If you then STOP, it eases back OUT to where you were (a transient peek).
 *   - But if you keep at it for a long while (sustained past the commit
 *     threshold), it treats that as "you're working here" and KEEPS the element
 *     framed — no drift back.
 *   - Selecting code frames the target immediately; deselecting within a window
 *     glides back to the prior viewport.
 *   - Editing a process frames the whole region the process spans (its target is
 *     a set of module ids, not one node).
 *
 * Everything here is pure and DOM-free: timestamps are injected (`at`), the
 * current viewport arrives inside `scene`, and each call returns an
 * `OperatorDecision` the binder applies. Quiet detection is cooperative — the
 * binder schedules `tick()` calls using `nextQuietCheck()`. That keeps the whole
 * temporal policy unit-testable without a clock or a browser.
 */
import type {
  CameraOperator,
  FrameTarget,
  OperatorDecision,
  OperatorScene,
  OperatorStayReason,
} from "./operator.js";
import type { Viewport } from "./viewport.js";

export interface FocusTuning {
  /** Accumulated editing time before the view eases in (skip micro edits). */
  readonly editFrameAfterMs: number;
  /** No activity for this long ⇒ the session settles (drift back or commit). */
  readonly settleQuietMs: number;
  /** A session whose active editing reaches this is "committed" — it keeps the
   *  element framed on settle instead of drifting back. */
  readonly commitActiveMs: number;
  /** A gap longer than this starts a brand-new session (the old one is over). */
  readonly sessionGapMs: number;
  /** Deselecting within this long of a selection-frame arms the glide back. */
  readonly selectionReturnMs: number;
  /**
   * Grace after a deselect before the armed glide-back actually fires. A caret
   * move into the framed node's body collapses the selection — without the
   * grace, the view flies away the instant before you start typing. Typing
   * within the grace cancels the return (the selection hands over to an edit
   * session that inherits the same "home" to drift back to later).
   */
  readonly selectionGraceMs: number;
  /**
   * A fresh edit session that lands on the SAME node(s) an edit session ended
   * on within this window inherits the prior session's accumulated `activeMs`.
   * Bridges the natural type→ponder→type rhythm: a thinking pause longer than
   * `sessionGapMs` no longer resets commit progress or re-plays the frame delay.
   */
  readonly continueWithinMs: number;
}

export const DEFAULT_FOCUS_TUNING: FocusTuning = {
  editFrameAfterMs: 1500,
  settleQuietMs: 5000,
  commitActiveMs: 10000,
  sessionGapMs: 5000,
  selectionReturnMs: 6000,
  selectionGraceMs: 5000,
  continueWithinMs: 30000,
};

export interface FocusDirector {
  /**
   * An edit landed on `ids` (the changed node, or a process's spanned modules
   * when `region` is true). Returns a frame once editing is sustained, a stay
   * while it's still micro, etc.
   */
  edit(ids: readonly string[], scene: OperatorScene, at: number, region?: boolean): OperatorDecision;
  /**
   * The caret moved to sit inside `ids` (the module chain the cursor is in;
   * empty when it's outside any declaration). Records whether the caret is
   * still inside the live edit session's framed target, so a quiet session
   * with the caret parked in its node HOLDS (the user is reading/pondering)
   * rather than drifting back. No decision — the binder feeds it and reschedules.
   */
  caret(ids: readonly string[], at: number): void;
  /**
   * Spotlight hold — the "point faster" gesture. `spotlightStart` supersedes the
   * current session (an edit session feeds continuation, so resumed typing after
   * the peek inherits its progress) and arms a hold with NO home captured yet.
   * `spotlightTarget` frames `ids` immediately (no commitment thresholds, loose
   * gate still applies) and lazily captures home on the first real move — a hold
   * that never retargets is a guaranteed no-op. `spotlightEnd` glides back to the
   * captured home. A `spotlightTarget` with no live spotlight session is a no-op
   * (post-gesture events can't resurrect it).
   */
  spotlightStart(at: number): void;
  spotlightTarget(ids: readonly string[], scene: OperatorScene, at: number): OperatorDecision;
  /**
   * Spotlight hold, overview variant (⌥⇧) — frame a whole SPACE instead of a
   * node (the "where am I?" glance). Same lazy-home / restore machinery as
   * `spotlightTarget`; a no-op without a live spotlight session.
   */
  spotlightSpace(spacePath: readonly string[], scene: OperatorScene, at: number): OperatorDecision;
  spotlightEnd(scene: OperatorScene, at: number): OperatorDecision;
  /**
   * Scope-dial press — frame `target` (a ring: node / neighbourhood / space)
   * immediately, bypassing suppression. The dial is a MODE, not a peek, so it
   * neutralises any live session's pending drift-back (drops its home) — the
   * uncommitted settle won't snap back to the pre-dial view.
   */
  dialFrame(target: FrameTarget, scene: OperatorScene, at: number): OperatorDecision;
  /** The user selected code covering `ids` — frame immediately (peek). */
  selection(ids: readonly string[], scene: OperatorScene, at: number): OperatorDecision;
  /**
   * The user cleared the selection. `graceful` says where the caret LANDED:
   * true = inside the framed node (they're about to type there) → arm a
   * grace-delayed glide-back (fired by `tick` after `selectionGraceMs`, unless
   * typing takes over first); false = caret moved away (another module / top
   * level) → glide back immediately, no grace.
   */
  selectionCleared(scene: OperatorScene, at: number, graceful?: boolean): OperatorDecision;
  /** A scheduled quiet check — drift back / commit once activity has stopped. */
  tick(scene: OperatorScene, at: number): OperatorDecision;
  /** The user drove the board — abandon any pending drift-back; they own it. */
  userGesture(at: number): void;
  /** Forget everything (workspace swap). */
  reset(): void;
  /** ms from `at` until the next `tick()` should fire, or null if idle. The
   *  binder uses this to schedule the quiet check. */
  nextQuietCheck(at: number): number | null;
}

interface Session {
  kind: "edit" | "selection" | "spotlight";
  firstAt: number;
  lastAt: number;
  /** Accumulated active time (sum of inter-edit gaps under `sessionGapMs`). */
  activeMs: number;
  framed: boolean;
  framedAt: number;
  /** The RAW anchor ids this session is about (the edited/selected node[s],
   *  never the ring-expanded set) — used for continuation matching and the
   *  caret-inside test. */
  targetIds: readonly string[];
  /** Whether the caret currently sits inside `targetIds`. Typing sets it (the
   *  caret is where you type); a `caret()` landing elsewhere clears it. A quiet
   *  edit session with this set postpones its settle. */
  caretInside: boolean;
  /** Selection sessions only: when the selection was cleared — arms the
   *  grace-delayed glide-back (fires `selectionGraceMs` later unless typing
   *  takes over first). */
  clearedAt?: number;
}

/** Continuation memory: the last edit session that ended in a way that should
 *  let a same-node follow-up inherit its progress (settle / gap, NOT a manual
 *  gesture). */
interface EndedEdit {
  ids: readonly string[];
  activeMs: number;
  endedAt: number;
}

function intersects(a: readonly string[], b: readonly string[]): boolean {
  return a.some((x) => b.includes(x));
}

const stay = (reason: OperatorStayReason): OperatorDecision => ({ kind: "stay", reason });

function samePath(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((s, i) => s === b[i]);
}

export function createFocusDirector(
  operator: CameraOperator,
  tuning: FocusTuning = DEFAULT_FOCUS_TUNING,
): FocusDirector {
  // The viewport to drift back to, captured the moment a session first frames.
  // Strictly session-scoped: set on first frame, cleared whenever the session
  // ends (so it can never leak across sessions).
  let home: Viewport | null = null;
  let session: Session | null = null;
  // The last edit session that ended cleanly (settle / gap) — a same-node edit
  // within `continueWithinMs` inherits its `activeMs`. Never written on a manual
  // gesture (gesture = deliberate fresh start).
  let lastEnded: EndedEdit | null = null;

  /** End the current session. When `recordAt` is given and the session is an
   *  edit, remember it for same-node continuation; a gesture-driven end passes
   *  nothing so the look-away starts fresh next time. */
  function endSession(recordAt?: number): void {
    if (recordAt !== undefined && session?.kind === "edit") {
      lastEnded = { ids: session.targetIds, activeMs: session.activeMs, endedAt: recordAt };
    }
    session = null;
    home = null;
  }

  /** Accumulated active time to inherit for a fresh edit on `ids` — a recent
   *  same-node session's total (continuation). Consumes the memory so a later
   *  gesture-ended session can't re-inherit stale progress. */
  function inheritedActiveMs(ids: readonly string[], at: number): number {
    if (
      lastEnded &&
      at - lastEnded.endedAt <= tuning.continueWithinMs &&
      intersects(ids, lastEnded.ids)
    ) {
      const v = lastEnded.activeMs;
      lastEnded = null;
      return v;
    }
    return 0;
  }

  /** Start a fresh edit session on `ids`, inheriting continuation progress. */
  function startEditSession(ids: readonly string[], at: number): Session {
    return {
      kind: "edit",
      firstAt: at,
      lastAt: at,
      activeMs: inheritedActiveMs(ids, at),
      framed: false,
      framedAt: at,
      targetIds: ids,
      caretInside: true,
    };
  }

  /** Frame `ids` via the operator's selection geometry (gentle: stays put if the
   *  target is already comfortably in view). Captures `home` + marks framed on
   *  the first real move. */
  function frame(
    ids: readonly string[],
    scene: OperatorScene,
    at: number,
    reason: "typing-followed" | "process-span" | "reveal" | "selection",
  ): OperatorDecision {
    // Once this session has framed its target, switch the operator to the
    // tight tracking gate: the camera holds the node, so it follows the node's
    // size both ways (a note that grows zooms out, one that shrinks zooms in).
    const tracking = session?.framed === true;
    // Scope-dial expansion happens HERE, at frame time — the session keeps the
    // raw ids for continuation/caret-inside, but the camera frames the current
    // altitude ring (node / neighbourhood / space).
    const target: FrameTarget = scene.expandTarget ? scene.expandTarget(ids) : { ids };
    const decision = decideTarget(target, scene, at, tracking, false, false);
    if (decision.kind !== "move") return decision; // stay (disabled / suppressed / on-screen)
    if (session && !session.framed) {
      if (home === null) home = scene.inFlightDest ?? scene.viewport;
      session.framed = true;
      session.framedAt = at;
    }
    return { ...decision, reason };
  }

  /** Ask the operator to frame a target — ids (selection gate) or a space. */
  function decideTarget(
    target: FrameTarget,
    scene: OperatorScene,
    at: number,
    tracking: boolean,
    bypassSuppression: boolean,
    bypassEnabled: boolean,
  ): OperatorDecision {
    if ("spacePath" in target) {
      return operator.decide(
        { type: "space", path: target.spacePath, at, tracking, bypassSuppression, bypassEnabled },
        scene,
      );
    }
    return operator.decide(
      { type: "selection", ids: [...target.ids], at, tracking, bypassSuppression, bypassEnabled },
      scene,
    );
  }

  /** Frame `target` inside the live spotlight session: bypass suppression AND the
   *  disabled gate (the hold is an explicit "show me", available with autopilot
   *  off), lazily capture home on the first real move, and retarget freely. A
   *  no-op without a live spotlight session (post-gesture events can't resurrect
   *  it). Shared by the node hold (`spotlightTarget`) and the overview glance
   *  (`spotlightSpace`). */
  function spotlightFrame(
    target: FrameTarget,
    scene: OperatorScene,
    at: number,
  ): OperatorDecision {
    if (session?.kind !== "spotlight") return stay("empty");
    session.lastAt = at;
    if ("ids" in target) session.targetIds = target.ids;
    const decision = decideTarget(target, scene, at, session.framed, true, true);
    if (decision.kind !== "move") return decision; // stay (already comfortable)
    if (home === null) home = scene.inFlightDest ?? scene.viewport; // lazy capture
    session.framed = true;
    session.framedAt = at;
    return decision;
  }

  function restore(target: Viewport, scene: OperatorScene): OperatorDecision {
    return {
      kind: "move",
      viewport: target,
      reason: "restore",
      animate: samePath(target.focalPath, scene.viewport.focalPath),
    };
  }

  return {
    edit(ids, scene, at, region = false) {
      if (ids.length === 0) return stay("empty");
      if (session?.kind === "selection") {
        // Typing right after a selection (e.g. caret moved into the framed
        // node's body, collapsing the selection) — the selection hands over to
        // an edit session that INHERITS its frame state and `home`. Any armed
        // glide-back is cancelled (typing is the "I'm staying" signal); a
        // transient edit still drifts back to the same home when it settles.
        session = {
          kind: "edit",
          firstAt: at,
          lastAt: at,
          activeMs: 0,
          framed: session.framed,
          framedAt: session.framedAt,
          targetIds: ids,
          caretInside: true,
        };
      } else if (session?.kind === "spotlight") {
        // Point → type: the hold converts into an edit session anchored at the
        // edit target, KEEPING the spotlight's captured home as its drift-back
        // home (so a later settle returns to where the hold began, not the
        // pointed node). Inherits continuation active time (a hold-peek mid-edit
        // is a thinking pause). The subsequent keyup restores nothing — no
        // spotlight session remains.
        session = {
          kind: "edit",
          firstAt: at,
          lastAt: at,
          activeMs: inheritedActiveMs(ids, at),
          framed: session.framed,
          framedAt: session.framedAt,
          targetIds: ids,
          caretInside: true,
        };
      } else if (!session || at - session.lastAt > tuning.sessionGapMs) {
        // Ending the prior edit session records it for continuation; the fresh
        // session may then inherit its accumulated active time (same node,
        // within the window) so a ponder pause doesn't reset commit progress.
        if (session) endSession(at);
        session = startEditSession(ids, at);
      } else {
        session.activeMs += at - session.lastAt;
        session.lastAt = at;
        session.targetIds = ids;
        session.caretInside = true;
      }
      // Don't move for micro edits — wait until editing is sustained.
      if (!session.framed && session.activeMs < tuning.editFrameAfterMs) {
        return stay("await-commitment");
      }
      return frame(ids, scene, at, region ? "process-span" : "typing-followed");
    },

    caret(ids, at) {
      // Only meaningful for a live edit session: mark whether the caret is still
      // inside the framed target. `at` is unused today (kept for symmetry with
      // the timestamped API and future dwell accounting).
      void at;
      if (session?.kind !== "edit") return;
      session.caretInside = intersects(ids, session.targetIds);
    },

    spotlightStart(at) {
      // A hold supersedes any current session. Ending an edit session records it
      // for continuation (resumed typing after the peek inherits its progress);
      // a selection/spotlight end records nothing. Home is captured lazily on the
      // first real move, so a hold that never retargets is a guaranteed no-op.
      endSession(at);
      session = {
        kind: "spotlight",
        firstAt: at,
        lastAt: at,
        activeMs: 0,
        framed: false,
        framedAt: at,
        targetIds: [],
        caretInside: false,
      };
    },

    spotlightTarget(ids, scene, at) {
      if (ids.length === 0) return stay("empty");
      return spotlightFrame({ ids }, scene, at);
    },

    spotlightSpace(spacePath, scene, at) {
      return spotlightFrame({ spacePath }, scene, at);
    },

    spotlightEnd(scene, at) {
      if (session?.kind !== "spotlight") return stay("empty");
      const target = home;
      endSession();
      // Restore only if a real move captured a home; a no-retarget hold just ends.
      return target ? restore(target, scene) : stay("empty");
    },

    dialFrame(target, scene, at) {
      const tracking = session?.framed === true;
      const decision = decideTarget(target, scene, at, tracking, true, true);
      if (decision.kind !== "move") return decision;
      // The dial is a MODE: drop any live session's home so its pending settle
      // won't snap back to the pre-dial view; the session keeps following (at the
      // new altitude) and re-captures home at its next frame.
      home = null;
      if (session) {
        session.framed = true;
        session.framedAt = at;
      }
      return decision;
    },

    selection(ids, scene, at) {
      if (ids.length === 0) return stay("empty");
      endSession();
      session = {
        kind: "selection",
        firstAt: at,
        lastAt: at,
        activeMs: 0,
        framed: false,
        framedAt: at,
        targetIds: ids,
        caretInside: false,
      };
      return frame(ids, scene, at, ids.length > 1 ? "selection" : "reveal");
    },

    selectionCleared(scene, at, graceful = true) {
      if (
        session &&
        session.kind === "selection" &&
        session.framed &&
        home &&
        at - session.framedAt <= tuning.selectionReturnMs
      ) {
        if (!graceful) {
          // Caret landed AWAY from the framed node — the peek is over; glide
          // back immediately.
          const target = home;
          endSession();
          return restore(target, scene);
        }
        // Caret stayed inside the framed node — don't fly away yet. Arm the
        // glide-back and give the user the grace window to start typing (a
        // caret move into the node's body collapses the selection moments
        // before the first keystroke). `tick` fires the return once the grace
        // passes with no typing.
        session.clearedAt = at;
        return stay("settling");
      }
      // Outside the window (or nothing framed) — keep the view, end the peek.
      if (session && session.kind === "selection") endSession();
      return stay("empty");
    },

    tick(scene, at) {
      if (!session) return stay("empty");
      // A held spotlight never settles on its own — it waits for release, a
      // gesture, or an edit converting it.
      if (session.kind === "spotlight") return stay("settling");
      if (session.kind === "selection") {
        // Armed glide-back: fire once the grace passes. A live (un-cleared)
        // selection never settles on its own — it waits for a deselect or for
        // typing to take over.
        if (session.clearedAt === undefined) return stay("settling");
        if (at - session.clearedAt < tuning.selectionGraceMs) return stay("settling");
        const target = home;
        endSession();
        return target ? restore(target, scene) : stay("empty");
      }
      if (at - session.lastAt < tuning.settleQuietMs) return stay("settling");
      // Quiet. If the caret is still parked inside the framed node, the user is
      // reading/pondering, not done — postpone the settle (wall-clock dwell
      // since the frame is the clock, so a parked caret with zero events still
      // reaches the cap). Past the cap, settle IN PLACE: keep the view, drop
      // home. This prevents both the yo-yo (drift-back mid-thought) and a
      // surprise flight to a home captured minutes ago.
      if (session.framed && session.caretInside) {
        if (at - session.framedAt < tuning.commitActiveMs) return stay("settling");
        endSession(at);
        return stay("settling");
      }
      // Caret has left (or never framed) — settle the edit session normally.
      const committed = session.activeMs >= tuning.commitActiveMs;
      const driftBack = !committed && session.framed && home !== null;
      const target = home;
      endSession(at);
      return driftBack ? restore(target!, scene) : stay("settling");
    },

    userGesture(_at) {
      // A deliberate look-around ends the session WITHOUT recording continuation
      // — the next edit starts fresh, so one keystroke can't yank the camera back.
      endSession();
    },

    reset() {
      endSession();
      lastEnded = null;
    },

    nextQuietCheck(at) {
      if (!session) return null;
      if (session.kind === "spotlight") return null; // nothing scheduled while held
      if (session.kind === "selection") {
        // Nothing to schedule until the selection is cleared; then check at
        // the end of the grace window.
        if (session.clearedAt === undefined) return null;
        return Math.max(0, session.clearedAt + tuning.selectionGraceMs - at);
      }
      const quietAt = session.lastAt + tuning.settleQuietMs;
      if (session.framed && session.caretInside) {
        // A caret-held session postpones its settle to the caret-hold cap —
        // schedule the single tick where BOTH the quiet window and the cap have
        // passed, so a parked caret (no further events) still commits in place.
        return Math.max(0, Math.max(quietAt, session.framedAt + tuning.commitActiveMs) - at);
      }
      return Math.max(0, quietAt - at);
    },
  };
}
